import {FC, RefObject, useCallback, useEffect, useMemo, useState} from 'react';
import {Row} from '@components/Row';
import classNames from 'classnames';
import {Button, Col, Divider, Form, InfoText, InputGroup, PaymentLabel, SelectableToggleBox, Spinner, Typography} from '@components/index';
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl';
import {
    payUCardMethods,
    payUInternetBankingMethods,
    payUTechCompPaymentMethods,
    payUTransferMethods,
    walletMaximumPrice,
    walletMinimumPrice,
    walletPaymentMethod,
} from '@constants/Payment';
import {getCurrencySymbol, useLocale} from '@utils/locale';
import {asValue} from '@utils/query';
import {LocaleEnum, nonNullArray} from 'src/types/general';
import {
    Currency,
    OrderPackageReferenceType,
    PaymentMethod,
    PaymentChannel,
    SelectedPackageInput,
    PageInfoType,
    Exact,
    User,
} from '.cache/__types__';
import NextImage from 'next/image';
import {useUserWalletQuery} from '@gql/query/user/wallet.gql';
import {useSession} from 'next-auth/react';
import {useAvailablePaymentMethodsQuery} from '@gql/query/pamentMethods/available.gql';
import {useProfileAuthorizedUserQuery} from '@gql/query/user/profile.gql';
import {getGraphQlLocale} from '../../types/graphql/general';
import {useOrderMutation} from '@gql/mutation/package/order.gql';
import {walletChargeUri} from '@constants/Package';
import {getLocalizedRoute, routes} from '@utils/routes';
import {usePageInfoQuery} from '@gql/query/page/info.gql';
import Link from 'next/link';

export type PaymentType = 'card' | 'banking' | 'wallet' | 'tech-comp' | 'transfer' | 'test' | 'invoice';

export type PaymentProps = {
    currency: Currency;
    price: number;
    children?: never;
    onValid?: () => any;
    onInvalid?: () => any;
    onSuccess?: (id?: string) => any;
    submitRef?: RefObject<HTMLElement>;
    packages: Exact<SelectedPackageInput>[];
    voucher?: {
        name: string;
    };
    hideWallet?: boolean;
};

export const Payment: FC<PaymentProps> = ({
    currency,
    price,
    voucher,
    onValid,
    onInvalid,
    onSuccess,
    packages,
    submitRef,
    hideWallet = false,
}) => {
    const [isValid, setValid] = useState<boolean>(false);
    const [paymentType, setPaymentType] = useState<PaymentType>();
    const [paymentChannel, setPaymentChannel] = useState(PaymentChannel.Undefined);
    const [amount, setAmount] = useState(Number.NaN);
    const session = useSession();
    const locale = useLocale();
    const intl = useIntl();

    const {data: termsData} = usePageInfoQuery({
        variables: {
            locale: getGraphQlLocale(locale),
            type: PageInfoType.TermsAndConditions,
        },
    });

    const {data: walletData} = useUserWalletQuery({
        variables: {
            userId: session.data?.user.id,
        },
    });

    const {data} = useAvailablePaymentMethodsQuery({
        variables: {
            locale: getGraphQlLocale(locale),
        },
    });

    const authUser = useProfileAuthorizedUserQuery()?.data?.authUser as User;

    const [createOrder, {loading}] = useOrderMutation({refetchQueries: ['AuthorizedUser']});

    const handleSubmit = useCallback(() => {
        if (packages) {
            const p = packages.slice(0);

            const rechargeWallet = paymentType === walletPaymentMethod && paymentChannel !== PaymentChannel.Undefined;
            if (rechargeWallet) {
                p.push({
                    uri: walletChargeUri,
                    currency: currency,
                    price: amount,
                    referenceId: session.data?.user.id,
                    referenceType: OrderPackageReferenceType.User,
                });
            }

            createOrder({
                variables: {
                    order: {
                        amount: paymentType === walletPaymentMethod && paymentChannel !== PaymentChannel.Undefined ? amount : price,
                        currency: currency,
                        method:
                            paymentType === walletPaymentMethod && paymentChannel === PaymentChannel.Undefined
                                ? PaymentMethod.Wallet
                                : paymentType === 'invoice'
                                ? PaymentMethod.Invoice
                                : PaymentMethod.Payu,
                        channel:
                            paymentType === 'card'
                                ? payUCardMethods[0]
                                : paymentType === 'transfer'
                                ? payUTransferMethods[0]
                                : paymentType === 'test'
                                ? PaymentChannel.T
                                : paymentType === 'invoice'
                                ? PaymentChannel.Undefined
                                : paymentChannel,
                        packages: p,
                        voucherName: voucher?.name ?? undefined,
                        originLocale: getGraphQlLocale(locale),
                    },
                },
            }).then((data) => {
                if (data.data?.createOrder?.success) {
                    onSuccess && onSuccess(data.data?.createOrder?.paymentId as string);
                }
                if (data.data?.createOrder?.payu?.payu_order_response?.redirectUri) {
                    // PayU redirect
                    window.location.href = data.data?.createOrder?.payu?.payu_order_response?.redirectUri;
                }
            });
        }
    }, [paymentType, paymentChannel, amount, packages, createOrder, currency, price, session.data?.user, voucher, onSuccess, locale]);

    useEffect(() => {
        const invalid =
            !paymentType ||
            (['tech-comp', 'banking'].includes(paymentType) && paymentChannel === PaymentChannel.Undefined) ||
            (paymentType === 'wallet' && (walletData?.user?.walletBalance ?? 0) + (Number.isNaN(amount) ? 0 : amount) - price < 0) ||
            (paymentType === 'wallet' && Number.isInteger(amount) && amount < 500) ||
            (paymentType === 'wallet' && amount > 0 && paymentChannel === PaymentChannel.Undefined);

        setValid(!invalid);
    }, [paymentType, paymentChannel, amount, price, walletData?.user?.walletBalance]);

    useEffect(() => {
        if (isValid) {
            onValid && onValid();
        } else {
            onInvalid && onInvalid();
        }
    }, [isValid, onValid, onInvalid]);

    useEffect(() => {
        if (submitRef?.current) {
            const target = submitRef.current;
            const handler = handleSubmit;
            target.addEventListener('click', handler);

            return () => {
                target.removeEventListener('click', handler);
            };
        }
    }, [submitRef, handleSubmit]);

    const testPaymentAvailable = useMemo(() => {
        return !!data?.availablePaymentMethods?.payByLinks?.find((item) => item?.value === PaymentChannel.T);
    }, [data?.availablePaymentMethods?.payByLinks]);

    if (!data || !Number.isInteger(walletData?.user?.walletBalance) || !walletData?.user?.walletCurrency) {
        return null;
    }

    return (
        <>
            <Row className={classNames('g-5', 'mb-8')}>
                <Col xs={12}>
                    <Form.Check
                        type="radio"
                        name="payment"
                        id="payment-card"
                        value={'card'}
                        checked={paymentType === 'card'}
                        onChange={() => setPaymentType('card')}
                        asToggleButton
                    />

                    <SelectableToggleBox size={'sm'} selected={paymentType === 'card'}>
                        <SelectableToggleBox.Heading hideButton>
                            <Row className="align-items-center gy-2 flex-sm-nowrap">
                                <Col xs={12} sm="auto" className="flex-grow-1 flex-shrink-1 mb-last-0">
                                    <Typography as="p" variant="subheadline" className="mb-2">
                                        <SelectableToggleBox.Label htmlFor="payment-card">
                                            <FormattedMessage defaultMessage={'Platební karta'} />
                                        </SelectableToggleBox.Label>
                                    </Typography>
                                    <Typography as="p" variant="perex" color="grey-dark">
                                        <FormattedMessage defaultMessage={'Aktivace ihned'} />
                                    </Typography>
                                </Col>

                                <Col xs="auto" className="mb-last-0">
                                    <Typography as="p" variant="perex" color="grey-dark">
                                        <FormattedMessage defaultMessage={'Visa, Mastercard'} />
                                    </Typography>
                                </Col>
                            </Row>
                        </SelectableToggleBox.Heading>
                    </SelectableToggleBox>
                </Col>

                {!hideWallet && !!walletData?.user?.walletBalance && walletData.user.walletBalance > 0 && (
                    <Col xs={12}>
                        <Form.Check
                            type="radio"
                            name="payment"
                            id="payment-wallet"
                            value="wallet"
                            checked={paymentType === walletPaymentMethod}
                            onChange={() => setPaymentType('wallet')}
                            asToggleButton
                        />

                        <SelectableToggleBox
                            size={'sm'}
                            selected={paymentType === walletPaymentMethod}
                            open={paymentType === walletPaymentMethod}
                        >
                            <SelectableToggleBox.Heading hideButton>
                                <Row className="align-items-center gy-2 flex-sm-nowrap">
                                    <Col xs={12} sm="auto" className="flex-grow-1 flex-shrink-1 mb-last-0">
                                        <Typography as="p" variant="subheadline" className="mb-2">
                                            <SelectableToggleBox.Label htmlFor="payment-wallet">
                                                <FormattedMessage defaultMessage={'Elektronická peněženka'} />
                                            </SelectableToggleBox.Label>
                                        </Typography>
                                        <Typography as="p" variant="perex" color="grey-dark">
                                            <FormattedMessage defaultMessage={'Aktivace ihned'} />
                                        </Typography>
                                    </Col>
                                </Row>
                            </SelectableToggleBox.Heading>

                            {walletData.user.walletCurrency === currency && (
                                <SelectableToggleBox.Body>
                                    <Row className="align-items-center mb-5">
                                        <Col className="flex-grow-1">
                                            <Typography as="p" variant="perex" className="mb-0">
                                                <FormattedMessage defaultMessage={'Váš aktuální zůstatek peněženky'} />
                                            </Typography>
                                        </Col>

                                        <Col xs="auto">
                                            <Typography as="p" variant="perex" className="mb-0" fontWeight="semibold">
                                                <FormattedNumber
                                                    value={walletData.user.walletBalance ?? 0}
                                                    currency={walletData.user.walletCurrency}
                                                    style={'currency'}
                                                    maximumFractionDigits={0}
                                                />
                                            </Typography>
                                        </Col>
                                    </Row>

                                    <Row className="align-items-center mb-5">
                                        <Col className="flex-grow-1">
                                            <Typography as="p" variant="perex" className="mb-0">
                                                <FormattedMessage defaultMessage={'Zvolená platba'} />
                                            </Typography>
                                        </Col>

                                        <Col xs="auto">
                                            <Typography as="p" variant="perex" className="mb-0" fontWeight="semibold">
                                                <FormattedNumber
                                                    value={price}
                                                    style={'currency'}
                                                    currency={currency}
                                                    maximumFractionDigits={0}
                                                />
                                            </Typography>
                                        </Col>
                                    </Row>

                                    {(!walletData.user.walletBalance || walletData.user.walletBalance < price) && (
                                        <>
                                            <Divider className={classNames('my-4')} />

                                            <Row className="mb-5 gy-4 justify-content-between align-items-end">
                                                <Col xs={12}>
                                                    <Form.Label htmlFor="amount">
                                                        <Typography color="red">
                                                            <FormattedMessage defaultMessage={'Zvolte výši kreditu k dobití'} />
                                                        </Typography>
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <Form.Control
                                                            id="amount"
                                                            placeholder="Zadejte částku"
                                                            value={Number.isNaN(amount) ? '' : amount}
                                                            onChange={(event) => setAmount(parseInt(event.currentTarget.value))}
                                                        />
                                                        <InputGroup.Text>
                                                            {getCurrencySymbol(intl, walletData.user.walletCurrency)}
                                                        </InputGroup.Text>
                                                    </InputGroup>
                                                </Col>

                                                <Col xs={12}>
                                                    <Form.RangeSlider
                                                        min={walletMinimumPrice[walletData.user.walletCurrency]}
                                                        max={walletMaximumPrice[walletData.user.walletCurrency]}
                                                        defaultValue={[500]}
                                                        value={Number.isNaN(amount) ? 500 : amount}
                                                        onChange={(value: number | number[]) => setAmount(asValue(value))}
                                                        ariaLabel={['Částka']}
                                                    >
                                                        <Form.RangeSlider.Label>
                                                            <FormattedNumber
                                                                value={walletMinimumPrice[walletData.user.walletCurrency]}
                                                                style={'currency'}
                                                                currency={walletData.user.walletCurrency}
                                                                maximumFractionDigits={0}
                                                            />
                                                        </Form.RangeSlider.Label>
                                                        <Form.RangeSlider.Label>
                                                            <FormattedNumber
                                                                value={walletMaximumPrice[walletData.user.walletCurrency]}
                                                                style={'currency'}
                                                                currency={walletData.user.walletCurrency}
                                                                maximumFractionDigits={0}
                                                            />
                                                        </Form.RangeSlider.Label>
                                                    </Form.RangeSlider>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col xs={12}>
                                                    <Row className="align-items-center g-3">
                                                        {nonNullArray(data.availablePaymentMethods?.payByLinks ?? [])
                                                            .filter(
                                                                (t) =>
                                                                    t &&
                                                                    (payUTechCompPaymentMethods.includes(
                                                                        t.value ?? PaymentChannel.Undefined,
                                                                    ) ||
                                                                        payUCardMethods.includes(t.value ?? PaymentChannel.Undefined) ||
                                                                        payUInternetBankingMethods.includes(
                                                                            t.value ?? PaymentChannel.Undefined,
                                                                        )),
                                                            )
                                                            .map((t) => (
                                                                <Col key={t.name} xs={'auto'}>
                                                                    <Form.Check
                                                                        type="radio"
                                                                        name="payment-wallet-variant"
                                                                        id={'payment-wallet-variant-' + t?.name}
                                                                        value={t?.value ?? ''}
                                                                        onChange={() =>
                                                                            setPaymentChannel(t?.value ?? PaymentChannel.Undefined)
                                                                        }
                                                                        checked={paymentChannel === t?.value}
                                                                        label={
                                                                            <PaymentLabel
                                                                                image={
                                                                                    <NextImage
                                                                                        src={t?.brandImageUrl ?? ''}
                                                                                        layout="fill"
                                                                                        objectFit="contain"
                                                                                        objectPosition="0 50%"
                                                                                    />
                                                                                }
                                                                            >
                                                                                {t?.name}
                                                                            </PaymentLabel>
                                                                        }
                                                                    />
                                                                </Col>
                                                            ))}
                                                    </Row>
                                                </Col>
                                            </Row>
                                        </>
                                    )}

                                    <Divider className={classNames('my-4')} />

                                    <Row className="align-items-center mb-5">
                                        <Col className="flex-grow-1">
                                            <Typography as="p" variant="perex" className="mb-0">
                                                <FormattedMessage defaultMessage={'Zůstatek po zaplacení'} />
                                            </Typography>
                                        </Col>

                                        <Col xs="auto">
                                            <Typography as="p" variant="perex" className="mb-0" fontWeight="semibold">
                                                <FormattedNumber
                                                    value={
                                                        (walletData.user.walletBalance ?? 0) + (Number.isNaN(amount) ? 0 : amount) - price
                                                    }
                                                    style={'currency'}
                                                    currency={walletData.user.walletCurrency}
                                                    maximumFractionDigits={0}
                                                />
                                            </Typography>
                                        </Col>
                                    </Row>
                                </SelectableToggleBox.Body>
                            )}

                            {walletData.user.walletCurrency !== currency && (
                                <SelectableToggleBox.Body>
                                    <Row className="align-items-center mb-5">
                                        <Col className="flex-grow-1">
                                            <Typography as="p" variant="perex" className="mb-0">
                                                <FormattedMessage defaultMessage={'Vaše peněženka není v uvedené měně'} />
                                            </Typography>
                                        </Col>
                                    </Row>
                                </SelectableToggleBox.Body>
                            )}
                        </SelectableToggleBox>
                    </Col>
                )}

                {nonNullArray(data.availablePaymentMethods?.payByLinks ?? []).filter((t) =>
                    payUInternetBankingMethods.includes(t.value ?? PaymentChannel.Undefined),
                ).length > 0 && (
                    <Col xs={12}>
                        <Form.Check
                            type="radio"
                            name="payment"
                            id="payment-banking"
                            value="banking"
                            checked={paymentType === 'banking'}
                            onChange={() => setPaymentType('banking')}
                            asToggleButton
                        />

                        <SelectableToggleBox size={'sm'} selected={paymentType === 'banking'} open={paymentType === 'banking'}>
                            <SelectableToggleBox.Heading hideButton>
                                <Row className="align-items-center gy-2 flex-sm-nowrap">
                                    <Col xs={12} sm="auto" className="flex-grow-1 flex-shrink-1 mb-last-0">
                                        <Typography as="p" variant="subheadline" className="mb-2">
                                            <SelectableToggleBox.Label htmlFor="payment-banking">
                                                <FormattedMessage defaultMessage={'Internetové bankovnictví'} />
                                            </SelectableToggleBox.Label>
                                        </Typography>
                                        <Typography as="p" variant="perex" color="grey-dark">
                                            <FormattedMessage defaultMessage={'Aktivace ihned'} />
                                        </Typography>
                                    </Col>
                                </Row>
                            </SelectableToggleBox.Heading>

                            <SelectableToggleBox.Body>
                                <Row className="g-5">
                                    {nonNullArray(data.availablePaymentMethods?.payByLinks ?? [])
                                        .filter((t) => payUInternetBankingMethods.includes(t.value ?? PaymentChannel.Undefined))
                                        .map((t) => (
                                            <Col xs={12} key={t.name}>
                                                <Form.Check
                                                    type="radio"
                                                    name="payment-banking-variant"
                                                    id={'payment-banking-variant-' + t?.name}
                                                    value={t?.value ?? ''}
                                                    onChange={() => setPaymentChannel(t?.value ?? PaymentChannel.Undefined)}
                                                    checked={paymentChannel === t?.value}
                                                    label={
                                                        <PaymentLabel
                                                            image={
                                                                <NextImage
                                                                    src={t?.brandImageUrl ?? ''}
                                                                    layout="fill"
                                                                    objectFit="contain"
                                                                    objectPosition="0 50%"
                                                                />
                                                            }
                                                        >
                                                            {t?.name}
                                                        </PaymentLabel>
                                                    }
                                                />
                                            </Col>
                                        ))}
                                </Row>
                            </SelectableToggleBox.Body>
                        </SelectableToggleBox>
                    </Col>
                )}

                {locale !== LocaleEnum.sk && (
                    <Col xs={12}>
                        <Form.Check
                            type="radio"
                            name="payment"
                            id="payment-transfer"
                            value={'transfer'}
                            checked={paymentType === 'transfer'}
                            onChange={() => setPaymentType('transfer')}
                            asToggleButton
                        />

                        <SelectableToggleBox size={'sm'} selected={paymentType === 'transfer'}>
                            <SelectableToggleBox.Heading hideButton>
                                <Row className="align-items-center gy-2 flex-sm-nowrap">
                                    <Col xs={12} sm="auto" className="flex-grow-1 flex-shrink-1 mb-last-0">
                                        <Typography as="p" variant="subheadline" className="mb-2">
                                            <SelectableToggleBox.Label htmlFor="payment-transfer">
                                                <FormattedMessage defaultMessage={'Bankovní převod'} />
                                            </SelectableToggleBox.Label>
                                        </Typography>
                                    </Col>
                                </Row>
                            </SelectableToggleBox.Heading>
                        </SelectableToggleBox>
                    </Col>
                )}

                {authUser?.manualInvoices && (
                    <Col xs={12}>
                        <Form.Check
                            type="radio"
                            name="payment"
                            id="payment-invoice"
                            value="invoice"
                            checked={paymentType === 'invoice'}
                            onChange={() => setPaymentType('invoice')}
                            asToggleButton
                        />

                        <SelectableToggleBox size="sm" selected={paymentType === 'invoice'}>
                            <SelectableToggleBox.Heading hideButton>
                                <Row className="align-items-center gy-2 flex-sm-nowrap">
                                    <Col xs={12} sm="auto" className="flex-grow-1 flex-shrink-1 mb-last-0">
                                        <Typography as="p" variant="subheadline" className="mb-2">
                                            <SelectableToggleBox.Label htmlFor="payment-invoice">
                                                <FormattedMessage defaultMessage="Platba na fakturu" />
                                            </SelectableToggleBox.Label>
                                        </Typography>
                                    </Col>
                                </Row>
                            </SelectableToggleBox.Heading>
                        </SelectableToggleBox>
                    </Col>
                )}

                {nonNullArray(data.availablePaymentMethods?.payByLinks ?? []).filter((t) =>
                    payUTechCompPaymentMethods.includes(t.value ?? PaymentChannel.Undefined),
                ).length > 0 && (
                    <Col xs={12}>
                        <Form.Check
                            type="radio"
                            name="payment"
                            id="payment-tech-comp"
                            value="tech-comp"
                            checked={paymentType === 'tech-comp'}
                            onChange={() => setPaymentType('tech-comp')}
                            asToggleButton
                        />

                        <SelectableToggleBox size={'sm'} selected={paymentType === 'tech-comp'} open={paymentType === 'tech-comp'}>
                            <SelectableToggleBox.Heading hideButton>
                                <Row className="align-items-center gy-2 flex-sm-nowrap">
                                    <Col xs={12} sm="auto" className="flex-grow-1 flex-shrink-1 mb-last-0">
                                        <Typography as="p" variant="subheadline" className="mb-2">
                                            <SelectableToggleBox.Label htmlFor="payment-tech-comp">
                                                <FormattedMessage defaultMessage={'Apple Pay / Google Pay'} />
                                            </SelectableToggleBox.Label>
                                        </Typography>
                                        <Typography as="p" variant="perex" color="grey-dark">
                                            <FormattedMessage defaultMessage={'Aktivace ihned'} />
                                        </Typography>
                                    </Col>
                                </Row>
                            </SelectableToggleBox.Heading>

                            <SelectableToggleBox.Body>
                                <Row className="g-5">
                                    {nonNullArray(data.availablePaymentMethods?.payByLinks ?? [])
                                        .filter((t) => payUTechCompPaymentMethods.includes(t.value ?? PaymentChannel.Undefined))
                                        .map((t) => (
                                            <Col xs={12} key={t.name}>
                                                <Form.Check
                                                    type="radio"
                                                    name="payment-tech-comp-variant"
                                                    id={'payment-tech-comp-variant-' + t?.name}
                                                    value={t?.value ?? ''}
                                                    onChange={() => setPaymentChannel(t?.value ?? PaymentChannel.Undefined)}
                                                    checked={paymentChannel === t?.value}
                                                    label={
                                                        <PaymentLabel
                                                            image={
                                                                <NextImage
                                                                    src={t?.brandImageUrl ?? ''}
                                                                    layout="fill"
                                                                    objectFit="contain"
                                                                    objectPosition="0 50%"
                                                                />
                                                            }
                                                        >
                                                            {t?.name}
                                                        </PaymentLabel>
                                                    }
                                                />
                                            </Col>
                                        ))}
                                </Row>
                            </SelectableToggleBox.Body>
                        </SelectableToggleBox>
                    </Col>
                )}

                {testPaymentAvailable && (
                    <Col xs={12}>
                        <Form.Check
                            type="radio"
                            name="payment"
                            id="payment-test"
                            value={'test'}
                            checked={paymentType === 'test'}
                            onChange={() => setPaymentType('test')}
                            asToggleButton
                        />

                        <SelectableToggleBox size={'sm'} selected={paymentType === 'test'}>
                            <SelectableToggleBox.Heading hideButton>
                                <Row className="align-items-center gy-2 flex-sm-nowrap">
                                    <Col xs={12} sm="auto" className="flex-grow-1 flex-shrink-1 mb-last-0">
                                        <Typography as="p" variant="subheadline" className="mb-2">
                                            <SelectableToggleBox.Label htmlFor="payment-test">
                                                <FormattedMessage defaultMessage={'Testovací platba'} />
                                            </SelectableToggleBox.Label>
                                        </Typography>
                                    </Col>
                                </Row>
                            </SelectableToggleBox.Heading>
                        </SelectableToggleBox>
                    </Col>
                )}
            </Row>

            {termsData && (
                <InfoText iconName={false} className="mb-6" textAlign="center">
                    <FormattedMessage
                        defaultMessage={
                            'Kliknutím na tlačítko „Pokračovat na platbu“ vyjadřuji souhlas se <link>Zpracováním osobních údajů</link>.'
                        }
                        values={{
                            link: (chunks: any) => (
                                <Link
                                    href={getLocalizedRoute(routes.page, locale, {
                                        uri: [termsData.pageInfo?.uri, termsData.pageInfo?.suburi].filter((d) => !!d).join('/'),
                                    })}
                                >
                                    <a target="_blank">{chunks}</a>
                                </Link>
                            ),
                        }}
                    />
                </InfoText>
            )}

            {!submitRef?.current && termsData && (
                <Button className="w-100" disabled={!isValid} onClick={handleSubmit}>
                    {loading ? (
                        <Spinner animation={'border'} variant={'light'} showSymbol={false} size={'sm'} />
                    ) : (
                        <FormattedMessage defaultMessage={'Pokračovat na platbu'} />
                    )}
                </Button>
            )}
        </>
    );
};
